import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Alert, Typography } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { FruitifyContainer } from "../../../shared-components/Containers";
import GeneralLoading from "../../../shared-components/Loading";
import { AssignmentQuestionAnswerViewer } from "../../assignment/components/AssignmentQuestionAnswerViewer";
import { useApiClient } from "../../../api";
import { ModeratorMarkCard } from "../components/ModeratorMarkCard";

const ModeratorAnswerView = () => {
  const { answerId } = useParams();
  const navigate = useNavigate();
  const ApiClient = useApiClient();
  const apiClientRef = useRef(ApiClient);

  useEffect(() => {
    apiClientRef.current = ApiClient;
  }, [ApiClient]);

  const [answer, setAnswer] = useState(null);
  const [mappedMarks, setMappedMarks] = useState([]);
  const [currentEducationLevel, setCurrentEducationLevel] = useState(null);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnswerData = async () => {
      if (answerId) {
        setLoading(true);
        setError(null);
        try {
          const ans = await apiClientRef.current.moderator.getModeratorAnswer({
            answer: answerId,
          });
          if (ans) {
            setAnswer(ans);
            setCurrentEducationLevel(ans.question.educationLevel.id);
            setCurrentSubject(ans.question.subject.id);

            // Use moderator feedback if available, otherwise fall back to automated
            const feedbackSource =
              ans.moderatorFeedback || ans.automatedFeedback;
            if (feedbackSource?.assessmentObjectiveFeedback) {
              const marks = feedbackSource.assessmentObjectiveFeedback.map(
                (aoFeedback) => ({
                  [aoFeedback.assessmentObjective.id]: aoFeedback.mark,
                }),
              );
              setMappedMarks(marks);
            }
          }
        } catch (err) {
          setError(err.message);
          enqueueSnackbar(`Error loading answer: ${err.message}`, {
            variant: "error",
          });
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAnswerData();
  }, [answerId]);

  const handleSkipAndFetchNext = async () => {
    if (currentEducationLevel && currentSubject) {
      setSubmitting(true);
      try {
        // First, skip the current answer
        await ApiClient.moderator.skipModeratorAnswer({
          answer: answerId,
        });

        // Then fetch the next answer
        const ans = await ApiClient.moderator.getNextModeratorAnswer({
          educationLevel: currentEducationLevel,
          subject: currentSubject,
          currentAnswer: answerId,
        });
        if (ans) {
          navigate(`/moderator/answer/${ans.id}`);
        } else {
          enqueueSnackbar("No more answers available", { variant: "info" });
          navigate("/moderator/dashboard");
        }
      } catch (err) {
        enqueueSnackbar(`Error: ${err.message}`, { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleFetchNext = async () => {
    if (currentEducationLevel && currentSubject) {
      setLoading(true);
      try {
        const ans = await ApiClient.moderator.getNextModeratorAnswer({
          educationLevel: currentEducationLevel,
          subject: currentSubject,
          currentAnswer: answerId,
        });
        if (ans) {
          navigate(`/moderator/answer/${ans.id}`);
        } else {
          enqueueSnackbar("No more answers available", { variant: "info" });
          navigate("/moderator/dashboard");
        }
      } catch (err) {
        enqueueSnackbar(`Error: ${err.message}`, { variant: "error" });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!answer) return;

    // Build AO marks object
    const aoMarks = {};
    mappedMarks.forEach((marksObj) => {
      Object.entries(marksObj).forEach(([aoId, mark]) => {
        aoMarks[aoId] = mark;
      });
    });

    setSubmitting(true);
    try {
      const result = await ApiClient.moderator.submitModeratorFeedback({
        answer: answer.id,
        aoMarks: JSON.stringify(aoMarks),
        feedback: "",
        areasToImprove: "",
      });
      if (result.success) {
        enqueueSnackbar("Moderator feedback submitted successfully!", {
          variant: "success",
        });
        await handleFetchNext();
      } else {
        enqueueSnackbar(result.message || "Failed to submit feedback", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(`Error: ${err.message}`, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const onMarkChange = (selectedAOId, newValue) => {
    setMappedMarks((prevMappedMarks) =>
      prevMappedMarks.map((marksObj) => {
        if (Object.prototype.hasOwnProperty.call(marksObj, selectedAOId)) {
          return {
            ...marksObj,
            [selectedAOId]: parseInt(newValue, 10),
          };
        }
        return marksObj;
      }),
    );
  };

  const totalMarks = mappedMarks.reduce((sum, marksObj) => {
    return (
      sum + Object.values(marksObj).reduce((objSum, mark) => objSum + mark, 0)
    );
  }, 0);

  const maxMarks =
    answer?.question?.markScheme?.objectives?.reduce(
      (sum, qo) => sum + (qo.marks || 0),
      0,
    ) || 0;

  if (loading) {
    return <GeneralLoading />;
  }

  if (error) {
    return (
      <FruitifyContainer>
        <Alert severity="error">Unable to load answer. {error}</Alert>
      </FruitifyContainer>
    );
  }

  if (!answer) {
    return (
      <FruitifyContainer>
        <Alert severity="info">No answer found.</Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/moderator/dashboard")}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </FruitifyContainer>
    );
  }

  const assignmentQuestion = {
    id: `${answer.assignment.id}-${answer.question.id}`,
    question: answer.question,
    assignment: answer.assignment,
  };

  return (
    <FruitifyContainer>
      <Button
        variant="card-action"
        startIcon={<ChevronLeft />}
        sx={{ mb: 2, background: "transparent" }}
        onClick={() => navigate("/moderator/dashboard")}
      >
        Back to Dashboard
      </Button>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Moderating Answer
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Assignment: {answer.assignment.name} | Subject:{" "}
          {answer.question.subject.name} | Education Level:{" "}
          {answer.question.educationLevel.name}
        </Typography>
      </Box>

      <AssignmentQuestionAnswerViewer
        assignmentQuestions={[assignmentQuestion]}
        studentAnswers={[answer]}
        studentAnswer={answer}
        setStudentAnswer={setAnswer}
        selectedAssignmentQuestion={assignmentQuestion}
        setSelectedAssignmentQuestion={() => {}}
        mark={totalMarks}
        onMarkChange={onMarkChange}
        onSuccessfulSubmit={() => {}}
        assignmentPreferences={{}}
        isModerator={true}
      />

      <ModeratorMarkCard totalMark={totalMarks} maxMark={maxMarks} />

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={submitting}
          fullWidth
        >
          {submitting ? "Submitting..." : "Submit & Fetch Next"}
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={handleSkipAndFetchNext}
          disabled={submitting}
        >
          Skip & Fetch Next
        </Button>
      </Box>
    </FruitifyContainer>
  );
};

export default ModeratorAnswerView;
